import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Box } from "@mui/material";
import { useDispatch } from "react-redux";
import { requestMarketData } from "../../Redux/Reducers/marketDataSlice";
import Loader from "../../component/Loader";
import { usePortfolioLayoutState } from "../../hooks/useOptimizedState";
import { downloadPortfolioExport } from "../Portfolio/utils/portfolioExport";
import BenchmarkTicker from "../PerformanceDashboardV2/components/BenchmarkTicker";
import PortfolioHero from "./components/PortfolioHero";
import PortfolioTableV2 from "./PortfolioTableV2";

const PortfolioDetailsV2 = ({ fundName, strategyName, exportRef }) => {
  const dispatch = useDispatch();
  const exportSnapshotRef = useRef({});

  const updateExportSnapshot = useCallback((partial) => {
    exportSnapshotRef.current = { ...exportSnapshotRef.current, ...partial };
  }, []);

  const handleExportDataChange = useCallback(
    (partial) => {
      updateExportSnapshot(partial);
    },
    [updateExportSnapshot]
  );

  const handleExport = useCallback(() => {
    downloadPortfolioExport(exportSnapshotRef.current);
  }, []);

  useEffect(() => {
    if (!exportRef) return;
    exportRef.current = handleExport;
  }, [exportRef, handleExport]);

  const {
    strategies,
    threshold,
    lots,
    trades,
    isStrategist,
    RawMarketData,
    livePrices,
  } = usePortfolioLayoutState();

  const [totals, setTotals] = useState([]);
  const timeoutRef = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [isMarketData, setIsMarketData] = useState(false);

  const longShortLabels = useMemo(
    () => ["", "%AUM", "P(L)", "%P(L)", "Signals PnL"],
    []
  );

  const finalStrategyName = useMemo(() => {
    if (isStrategist && strategies?.length > 0) {
      return strategies[0].name;
    }
    return strategyName;
  }, [isStrategist, strategies, strategyName]);

  const titleText = useMemo(() => {
    return isStrategist
      ? "Strategy Performance Summary"
      : "Funds Performance Summary";
  }, [isStrategist]);

  useEffect(() => {
    updateExportSnapshot({
      totals,
      titleText,
      longShortLabels,
      livePrices,
    });
  }, [totals, titleText, longShortLabels, livePrices, updateExportSnapshot]);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setIsMarketData(false);
      dispatch(
        requestMarketData({
          lots: lots,
          threshold: threshold,
          noRequest: true,
          trades: trades,
        })
      );
    }, 20000);

    return () => clearTimeout(timeoutRef.current);
  }, [RawMarketData, dispatch, lots, threshold, trades]);

  return (
    <>
      {isMarketData ? (
        <Loader msg={true} marketLoader={true} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <PortfolioHero totals={totals} />
          <BenchmarkTicker marketData={livePrices} />
          <PortfolioTableV2
            fundname={fundName}
            strategyname={finalStrategyName}
            setTotals={setTotals}
            onExportDataChange={handleExportDataChange}
            onExport={handleExport}
          />
        </Box>
      )}
    </>
  );
};

export default PortfolioDetailsV2;
